import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Icons } from '@monorepo/icons';
import { Flex, TextComponent } from '@monorepo/react-components';
import { IconsNames, PayoutStatus } from '@monorepo/types';

import styles from './status-tag.module.scss';

const STATUS_KEY_PREFIX = 'affiliatePortal.dashboard.status';

export enum StatusTranslationKey {
  Pending = `${STATUS_KEY_PREFIX}.${PayoutStatus.Pending}`,
  Cancelled = `${STATUS_KEY_PREFIX}.${PayoutStatus.Cancelled}`,
  ProcessingByTradingSystem = `${STATUS_KEY_PREFIX}.${PayoutStatus.ProcessingByTradingSystem}`,
  Paid = `${STATUS_KEY_PREFIX}.${PayoutStatus.Paid}`,
  Declined = `${STATUS_KEY_PREFIX}.${PayoutStatus.Declined}`,
  Processing = `${STATUS_KEY_PREFIX}.${PayoutStatus.Processing}`,
  Approved = `${STATUS_KEY_PREFIX}.${PayoutStatus.Approved}`,
  Scheduled = `${STATUS_KEY_PREFIX}.${PayoutStatus.Scheduled}`,
  Sent = `${STATUS_KEY_PREFIX}.${PayoutStatus.Sent}`,
  Failed = `${STATUS_KEY_PREFIX}.${PayoutStatus.Failed}`,
  PendingVerification = `${STATUS_KEY_PREFIX}.${PayoutStatus.PendingVerification}`,
}

const statusTranslationKey: Record<PayoutStatus, StatusTranslationKey> = {
  [PayoutStatus.Pending]: StatusTranslationKey.Pending,
  [PayoutStatus.ProcessingByTradingSystem]:
    StatusTranslationKey.ProcessingByTradingSystem,
  [PayoutStatus.Cancelled]: StatusTranslationKey.Cancelled,
  [PayoutStatus.Paid]: StatusTranslationKey.Paid,
  [PayoutStatus.Declined]: StatusTranslationKey.Declined,
  [PayoutStatus.Processing]: StatusTranslationKey.Processing,
  [PayoutStatus.Approved]: StatusTranslationKey.Approved,
  [PayoutStatus.Scheduled]: StatusTranslationKey.Scheduled,
  [PayoutStatus.Sent]: StatusTranslationKey.Sent,
  [PayoutStatus.Failed]: StatusTranslationKey.Failed,
  [PayoutStatus.PendingVerification]: StatusTranslationKey.PendingVerification,
};

interface StatusTagProps {
  status: PayoutStatus;
  date?: string;
  text?: string;
  translationKey?: string;
  iconName?: IconsNames;
  iconSize?: number;
  className?: string;
}

const StatusTag: FC<StatusTagProps> = ({
  status,
  date,
  text,
  translationKey,
  iconName,
  iconSize = 16,
  className,
}) => {
  const { t } = useTranslation('affiliate');

  const getStatusDisplay = (): string => {
    if (text) {
      return text;
    }

    if (translationKey) {
      return t(translationKey, translationKey);
    }

    const key = statusTranslationKey[status];

    if (status === PayoutStatus.Pending && date) {
      return `${t(key, status)} ${date}`;
    }

    return t(key, status);
  };

  const statusClass = status.toLowerCase();

  return (
    <Flex
      className={classNames(styles.statusTag, styles[statusClass], className)}
      align="center"
      justify={iconName ? 'flex-start' : 'center'}
      gap={4}
    >
      {iconName && (
        <Icons iconName={iconName} size={iconSize} className={styles.icon} />
      )}
      <TextComponent level={9} className={styles.statusText}>
        {getStatusDisplay()}
      </TextComponent>
    </Flex>
  );
};

export default StatusTag;
